import { Injectable, computed, inject, signal } from '@angular/core';

import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { getError } from 'src/app/api/services/api-utils.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ExceptionLoggingService } from 'src/app/components/logging/exception-logging.service';
import {
  CompanySelectedSource,
  TrackerService,
} from 'src/app/components/logging/tracker.service';
import { ListErrorHandlerService } from 'src/app/components/scrolling-list/services/list-error-handler.service';
import { AddressUtilsService } from 'src/app/shared/services/address-utils.service';
import { CompanyTreeCacheService } from './company-tree-cache.service';
import { CompanyTreeExpansionService } from './company-tree-expansion.service';
import {
  BranchState,
  BranchStatus,
  CompanyNode,
  CompanyTreeRow,
} from 'src/app/app-common/types/company-tree';

const BRANCH_PAGE_SIZE = 40;

const MISSING_USER_COMPANY_ERROR =
  'Your company could not be loaded. Please reload this page and try again.';

type CompanyLike = {
  id?: string;
  name?: string;
  parentId?: string | null;
  street?: string | null;
  city?: string | null;
  province?: string | null;
};

@Injectable()
export class CompanyTreeService {
  private readonly cache = inject(CompanyTreeCacheService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly companyState = inject(CompanyStateService);
  private readonly expansion = inject(CompanyTreeExpansionService);
  private readonly addressUtils = inject(AddressUtilsService);
  private readonly exceptionLogging = inject(ExceptionLoggingService);
  private readonly listErrorHandler = inject(ListErrorHandlerService);
  private readonly tracker = inject(TrackerService);

  // Remembered between panel openings; everything below is per-opening state.
  private readonly nodes = this.cache.nodes;
  private readonly branches = this.cache.branches;
  private readonly knownLeaves = this.cache.knownLeaves;

  private readonly expanded = signal(new Set<string>());
  // Search's own expansion set: never written into `expanded` and never
  // persisted, so opening a match to look at its children does not change
  // what the tree remembers (FR-035, FR-090).
  private readonly searchExpanded = signal(new Set<string>());
  private readonly selectedPath = signal<string[]>([]);
  private readonly userCompanyId = signal<string | null>(null);
  private readonly selectedCompanyId = signal<string | null>(null);
  private readonly initialStatusState = signal<BranchStatus>('idle');
  private readonly initialError = signal<string | null>(null);
  // Chain members whose hidden siblings the user revealed. Lasts one opening:
  // never persisted and cleared by `reset()` (FR-105).
  private readonly revealedState = signal(new Set<string>());

  readonly initialStatus = this.initialStatusState.asReadonly();
  readonly initialErrorMessage = this.initialError.asReadonly();

  /** The company the application is currently switched to, once it is known. */
  readonly selectedCompany = computed<CompanyNode | null>(() => {
    const id = this.selectedCompanyId();
    return (id && this.nodes().get(id)) || null;
  });

  /**
   * The root-to-selected path, root first, when the selected company is a
   * sub-company: `[userCompanyId, …ancestors, selectedId]`. Null when the
   * user's own company is selected, or when any link between consecutive
   * members is missing or wrong — a partial ancestor walk (a failed
   * `getCompany`) or a cyclic one (`resolveAncestorPath`'s own cycle guard)
   * can otherwise look like a complete path with a gap in it.
   */
  private readonly chain = computed<string[] | null>(() => {
    const userCompanyId = this.userCompanyId();
    const selectedId = this.selectedCompanyId();
    const path = this.selectedPath();

    if (
      !userCompanyId ||
      !selectedId ||
      selectedId === userCompanyId ||
      path.length === 0 ||
      path[0] !== userCompanyId
    ) {
      return null;
    }

    const full = [...path, selectedId];
    const nodes = this.nodes();
    for (let i = 1; i < full.length; i++) {
      const node = nodes.get(full[i]);
      if (!node || node.parentId !== full[i - 1]) {
        return null;
      }
    }

    return full;
  });

  readonly rows = computed(() => this.projectRows());

  /**
   * True while any branch appends a further page. Read from the branches rather
   * than the projected rows so it does not depend on what is currently visible.
   */
  readonly loadingMore = computed(() => {
    for (const branch of this.branches().values()) {
      if (branch.status === 'loading' && branch.children.length > 0) {
        return true;
      }
    }

    return false;
  });

  async initialize(): Promise<void> {
    this.reset();
    this.initialStatusState.set('loading');
    this.initialError.set(null);

    const userCompanyId = this.companyState.getUserCompanyId();
    const userCompany = this.companyState.getCopyOfUserCompany();
    if (!userCompanyId || !userCompany?.id) {
      this.initialError.set(MISSING_USER_COMPANY_ERROR);
      this.initialStatusState.set('error');
      return;
    }

    // Before the root node is cached: an exhausted snapshot clears the maps.
    const snapshot = this.cache.open(userCompanyId);

    this.userCompanyId.set(userCompanyId);
    this.cacheNode(this.toNode(userCompany, true));

    const selectedCompany = this.companyState.getCopyOfSelectedCompany();
    const selectedId = selectedCompany?.id ?? userCompanyId;
    this.selectedCompanyId.set(selectedId);

    // Requested alongside the ancestor walk rather than after it: the selected
    // company's children never depend on the walk, and in the branch view they
    // are the only list on screen. Settled straight away so a failure that
    // lands before the walk ends is never an unhandled rejection.
    const selectedLoad = Promise.allSettled([this.loadBranch(selectedId)]);

    const cachedPath =
      snapshot === 'empty'
        ? null
        : this.cachedAncestorPath(selectedId, userCompanyId);

    if (cachedPath) {
      // Synchronously, before the first await: a status set after one is a
      // change-detection pass too late and the panel flashes its spinner.
      this.selectedPath.set(cachedPath);
      this.initialStatusState.set('loaded');
    } else {
      const path = await this.resolveAncestorPath(
        selectedCompany,
        userCompanyId,
      );
      this.selectedPath.set(path);
    }

    // The selected company joins the expanded set alongside its ancestors
    // only once the whole chain is confirmed; a failed or cyclic walk falls
    // back to the plain ancestor path, the same as when it never reached a
    // sub-company at all.
    const chain = this.chain();
    this.applyExpansion(chain ?? this.selectedPath(), userCompanyId);

    // The root's list is requested only when the tree opens from the root. In
    // the branch view the root and every ancestor show just the next company
    // on the path, which the walk already cached, so their lists stay
    // unrequested until a reveal asks for one.
    let rootLoad: Promise<PromiseSettledResult<void>[]> | undefined;
    if (!chain) {
      rootLoad =
        selectedId === userCompanyId
          ? selectedLoad
          : Promise.allSettled([this.loadBranch(userCompanyId)]);
    }

    // The selected company's load settles before the restore walk reads its
    // children. Only the root's own failure blocks the panel; the selected
    // company's leaves that row with its own error and Retry, the same way
    // any other branch failure does.
    const [, rootResults] = await Promise.all([selectedLoad, rootLoad]);
    const rootResult = rootResults?.[0];
    if (rootResult?.status === 'rejected') {
      this.initialError.set(
        this.branches().get(userCompanyId)?.errorMessage ??
          this.listErrorHandler.processErrorCode(
            'Companies',
            'load',
            rootResult.reason,
          ),
      );
      this.initialStatusState.set('error');
      return;
    }

    const { restored, failed } =
      await this.loadVisibleExpandedBranches(userCompanyId);
    // Skipped in the branch view: the walk cannot reach the remembered
    // expansions under a hidden sibling, and pruning against it would delete
    // them from storage before the user ever gets to reveal that sibling.
    if (this.chain() === null) {
      this.pruneUnrestoredExpanded(restored, failed);
    }

    // Only a real fetch stamps the snapshot. Stamping on a hit would push the
    // freshness window out on every opening and make it immortal.
    if (snapshot === 'empty') {
      this.cache.markLoaded();
    }

    this.initialStatusState.set('loaded');

    if (snapshot === 'stale') {
      void this.refreshSnapshot();
    }
  }

  async toggle(id: string): Promise<void> {
    if (this.expanded().has(id)) {
      const next = new Set(this.expanded());
      next.delete(id);
      this.expanded.set(next);
      this.persistExpanded();
      return;
    }

    if (this.knownLeaves().has(id)) {
      return;
    }

    this.markExpanded(id);
    this.persistExpanded();

    try {
      await this.loadBranch(id);
    } catch {
      return;
    }
  }

  async loadMore(id: string): Promise<void> {
    try {
      await this.loadBranch(id, { append: true });
    } catch {
      return;
    }
  }

  /**
   * Expands or collapses a node of the search results tree. A sibling of
   * `toggle` that loads through the same branch cache but never touches
   * `expanded` or storage.
   */
  async toggleSearchNode(id: string): Promise<void> {
    if (this.searchExpanded().has(id)) {
      const next = new Set(this.searchExpanded());
      next.delete(id);
      this.searchExpanded.set(next);
      return;
    }

    if (this.knownLeaves().has(id)) {
      return;
    }

    const next = new Set(this.searchExpanded());
    next.add(id);
    this.searchExpanded.set(next);

    try {
      await this.loadBranch(id);
    } catch {
      return;
    }
  }

  clearSearchExpansion(): void {
    this.searchExpanded.set(new Set());
  }

  markKnownLeaves(ids: readonly string[]): void {
    if (ids.length === 0) {
      return;
    }

    this.knownLeaves.update((set) => new Set([...set, ...ids]));
  }

  async retry(id: string): Promise<void> {
    const existing = this.branches().get(id);
    if (existing?.status !== 'error') {
      return;
    }

    const append = existing.children.length > 0 && !!existing.cursor;
    try {
      await this.loadBranch(id, append ? { append: true } : undefined);
    } catch {
      return;
    }
  }

  /**
   * Shows the rest of an unrevealed chain member's children in place. A
   * no-op once it is already revealed, so a second activation of "…" (it
   * becomes the normal chevron) does nothing here.
   */
  async revealChildren(id: string): Promise<void> {
    const member = this.chainMemberInfo(id);
    if (!member || this.revealedState().has(id)) {
      return;
    }

    this.revealedState.update((set) => new Set(set).add(id));

    try {
      await this.loadBranch(id);
    } catch {
      return;
    }

    await this.loadVisibleExpandedBranches(id);
  }

  /**
   * Returns the tree to the view it opened with: every reveal is undone and
   * every company on the path is expanded again. Synchronous and request-free,
   * because everything it brings back is already cached, and it never writes
   * the expansion state, the same as the path's expansion on open.
   */
  hideChildren(id: string): void {
    if (!this.canHideChildren(id)) {
      return;
    }

    const chain = this.chain();
    this.revealedState.set(new Set());
    this.expanded.update((current) => new Set([...current, ...(chain ?? [])]));
  }

  async select(
    id: string,
    source: CompanySelectedSource,
    node?: CompanyNode,
  ): Promise<void> {
    const next = new Set(this.expanded());
    for (const ancestorId of this.ancestorsOf(id)) {
      next.delete(ancestorId);
      next.add(ancestorId);
    }
    this.expanded.set(next);
    this.persistExpanded();

    const known = this.nodes().get(id) ?? node;
    const userCompanyId = this.userCompanyId();
    this.tracker.companySelectedEvent(
      id,
      known?.name ?? '',
      id === userCompanyId,
      source,
    );

    try {
      await this.companyState.switchCompany(id);
    } catch (error) {
      console.error('Failed to load selected company.', error);
    }
  }

  private reset(): void {
    this.expanded.set(new Set());
    this.searchExpanded.set(new Set());
    this.selectedPath.set([]);
    this.userCompanyId.set(null);
    this.selectedCompanyId.set(null);
    this.initialStatusState.set('idle');
    this.initialError.set(null);
    this.revealedState.set(new Set());
  }

  private applyExpansion(path: readonly string[], userCompanyId: string): void {
    this.expanded.set(
      new Set<string>([
        ...this.expansion.read(userCompanyId),
        ...path,
        userCompanyId,
      ]),
    );
  }

  /**
   * The selected company's ancestor chain read out of the remembered nodes, or
   * null when any link is unknown and the API walk has to run instead.
   *
   * `ancestorsOf` cannot stand in for this: it breaks out on a missing node and
   * still prepends the root, reporting a partial chain as complete. That is
   * right for recording expansion on selection and wrong here, where a partial
   * chain would place the selection under the wrong branch.
   */
  private cachedAncestorPath(
    selectedId: string,
    userCompanyId: string,
  ): string[] | null {
    if (selectedId === userCompanyId) {
      return [];
    }

    let current = this.nodes().get(selectedId);
    if (!current) {
      return null;
    }

    const path: string[] = [];
    // A hierarchy that contains one of its own ancestors would otherwise walk
    // the same ids forever.
    const visited = new Set<string>([selectedId]);

    while (current.parentId && current.parentId !== userCompanyId) {
      const parentId = current.parentId;
      if (visited.has(parentId)) {
        break;
      }
      visited.add(parentId);

      const parent = this.nodes().get(parentId);
      if (!parent) {
        return null;
      }

      path.unshift(parentId);
      current = parent;
    }

    path.unshift(userCompanyId);
    return path;
  }

  /**
   * `id`'s place in the chain, and the next company on the path to the
   * selected company, when `id` is a chain member other than the selected
   * company itself — the selected company's own children are never hidden.
   */
  private chainMemberInfo(id: string): { nextId: string } | null {
    const chain = this.chain();
    if (!chain) {
      return null;
    }

    const index = chain.indexOf(id);
    if (index < 0 || index === chain.length - 1) {
      return null;
    }

    return { nextId: chain[index + 1] };
  }

  /**
   * A chain member whose children are still reduced to the next company on
   * the path, because the user has not revealed the rest.
   */
  private isUnrevealedChainMember(id: string): boolean {
    return !!this.chainMemberInfo(id) && !this.revealedState().has(id);
  }

  /**
   * The single source of truth for which children a branch shows: both the
   * projection and the restore walk (`loadVisibleExpandedBranches`) call it,
   * so the two can never disagree about what is on screen.
   */
  private visibleChildIds(id: string): string[] {
    const branch = this.branches().get(id);
    const branchChildren = branch?.children ?? [];

    const member = this.chainMemberInfo(id);
    if (!member) {
      return branchChildren;
    }

    if (!this.revealedState().has(id)) {
      // Whatever the branch holds or whether it has loaded: the path child is
      // already cached from the ancestor walk, so it renders instantly.
      return [member.nextId];
    }

    // Appended until its own page arrives, so revealing never makes the
    // selected company disappear (OQ-07); deduplicated once it does.
    return branchChildren.includes(member.nextId)
      ? branchChildren
      : [...branchChildren, member.nextId];
  }

  /**
   * True only for an unrevealed chain member, and optimistic — the same way
   * the chevron is optimistic until a branch proves to be a leaf — until its
   * branch is loaded, has no further page, and holds no renderable child
   * other than the next company on the path.
   */
  private hasHiddenChildren(id: string): boolean {
    const member = this.chainMemberInfo(id);
    if (!member || this.revealedState().has(id)) {
      return false;
    }

    return this.hasChildrenBesidesPath(id, member.nextId);
  }

  /**
   * True only for the user's own company once its hidden children are shown,
   * and only while there is something to hide again. Shares its test with
   * `hasHiddenChildren`, so "…" and the down arrow never disagree about the
   * same list.
   */
  private canHideChildren(id: string): boolean {
    const member = this.chainMemberInfo(id);
    if (
      !member ||
      id !== this.userCompanyId() ||
      !this.revealedState().has(id)
    ) {
      return false;
    }

    return this.hasChildrenBesidesPath(id, member.nextId);
  }

  /**
   * Optimistic — the same way the chevron is optimistic until a branch proves
   * to be a leaf — until the branch is loaded and has no further page.
   */
  private hasChildrenBesidesPath(id: string, nextId: string): boolean {
    const branch = this.branches().get(id);
    if (!branch || branch.status !== 'loaded' || branch.cursor) {
      return true;
    }

    return branch.children.some((childId) => childId !== nextId);
  }

  /**
   * Re-requests the first page of the root's branch and of every expanded
   * branch, whichever of them is already loaded, behind a panel that is
   * already on screen. Only a loaded list is refreshed: the branch view never
   * requests the root's list, and a refresh must not be what fetches it. A
   * refresh the user did not ask for never shows a spinner and never turns a
   * working panel into an error state, so a failure leaves the remembered
   * branch as it was and simply leaves the snapshot stale for the next opening.
   */
  private async refreshSnapshot(): Promise<void> {
    const userCompanyId = this.userCompanyId();
    if (!userCompanyId || !this.cache.beginRefresh()) {
      return;
    }

    const ids = [
      userCompanyId,
      ...[...this.expanded()].filter((id) => id !== userCompanyId),
    ].filter((id) => this.branches().get(id)?.status === 'loaded');

    let ok = true;
    for (const id of ids) {
      if (!this.cache.owns(userCompanyId)) {
        ok = false;
        break;
      }

      try {
        await this.loadBranch(id, { refresh: true });
      } catch {
        ok = false;
      }
    }

    this.cache.endRefresh(ok);
  }

  private async resolveAncestorPath(
    selected: CompanyLike | null | undefined,
    userCompanyId: string,
  ): Promise<string[]> {
    if (!selected?.id || selected.id === userCompanyId) {
      return [];
    }

    this.cacheNode(this.toNode(selected));

    const path: string[] = [];
    // A hierarchy that contains one of its own ancestors would otherwise keep
    // requesting the same companies and never resolve.
    const visited = new Set<string>([selected.id]);
    let current: CompanyLike = selected;

    while (current.parentId && current.parentId !== userCompanyId) {
      const parentId = current.parentId;
      if (visited.has(parentId)) {
        break;
      }
      visited.add(parentId);
      try {
        const parent = await this.companyApi.getCompany(parentId);
        this.cacheNode(this.toNode(parent));
        path.unshift(parent.id);
        current = parent;
      } catch (error) {
        this.handleAncestorFailure(error, parentId, userCompanyId);
        return path;
      }
    }

    path.unshift(userCompanyId);
    return path;
  }

  private handleAncestorFailure(
    error: unknown,
    parentId: string,
    userCompanyId: string,
  ): void {
    console.error('Failed to load company.', error);

    if (this.isForbiddenOrNotFound(error)) {
      this.expansion.prune(userCompanyId, parentId);
      return;
    }

    this.exceptionLogging.handle(error);
  }

  /**
   * Loads every persisted branch whose whole ancestor chain is expanded, level
   * by level, and reports the ids it reached alongside the ones whose load
   * failed. A failed branch is reached but never yields its children, so its
   * own persisted descendants go unvisited through no fault of the user.
   */
  private async loadVisibleExpandedBranches(
    rootId: string,
  ): Promise<{ restored: Set<string>; failed: Set<string> }> {
    // A hierarchy that contains one of its own ancestors would otherwise keep
    // repopulating the queue and never drain.
    const visited = new Set<string>([rootId]);
    const failed = new Set<string>();
    // `visibleChildIds` rather than the branch's own children: a remembered
    // expansion under a hidden sibling must not be fetched before that
    // sibling is revealed.
    let levelIds = this.visibleChildIds(rootId);

    while (levelIds.length > 0) {
      const expanded = this.expanded();
      const toLoad: string[] = [];
      for (const id of levelIds) {
        if (
          expanded.has(id) &&
          !visited.has(id) &&
          !this.knownLeaves().has(id)
        ) {
          visited.add(id);
          toLoad.push(id);
        }
      }

      await Promise.all(
        toLoad.map(async (id) => {
          // Walked through, never loaded: an unrevealed chain member shows
          // only the next company on the path, so its own list is not on
          // screen and must not be requested until a reveal asks for it.
          if (this.isUnrevealedChainMember(id)) {
            return;
          }

          try {
            await this.loadBranch(id);
          } catch {
            failed.add(id);
          }
        }),
      );

      const next: string[] = [];
      for (const id of toLoad) {
        next.push(...this.visibleChildIds(id));
      }
      levelIds = next;
    }

    return { restored: visited, failed };
  }

  /**
   * Drops persisted ids the restore never reached, because they sit inside a
   * collapsed ancestor. Kept, they would surface later as a branch that renders
   * expanded with nothing under it — its children were never loaded — so
   * opening it would take two clicks: one to collapse the stale state and one
   * to expand for real.
   *
   * Skipped entirely when any branch failed to load: an id under a failed
   * branch is indistinguishable here from one under a collapsed ancestor, and
   * pruning writes to storage, so a transient API error would permanently
   * discard an expansion the user chose. The stale-expansion cleanup is worth
   * forgoing in a session that already hit an error.
   */
  private pruneUnrestoredExpanded(
    restored: ReadonlySet<string>,
    failed: ReadonlySet<string>,
  ): void {
    if (failed.size > 0) {
      return;
    }

    const current = this.expanded();
    const next = new Set([...current].filter((id) => restored.has(id)));
    if (next.size === current.size) {
      return;
    }

    this.expanded.set(next);
    this.persistExpanded();
  }

  private async loadBranch(
    id: string,
    options?: { append?: boolean; refresh?: boolean },
  ): Promise<void> {
    const append = options?.append === true;
    const refresh = options?.refresh === true;
    const existing = this.branches().get(id);

    if (refresh) {
      if (existing?.status === 'loading') {
        return;
      }
    } else if (append) {
      if (!existing?.cursor || existing.status === 'loading') {
        return;
      }
    } else if (
      existing?.status === 'loading' ||
      existing?.status === 'loaded'
    ) {
      return;
    }

    if (!refresh) {
      this.setBranch(id, {
        status: 'loading',
        children: existing?.children ?? [],
        cursor: existing?.cursor ?? null,
      });
    }

    try {
      const page = await this.companyApi.getCompanies(
        {
          companyId: id,
          sortBy: 'name',
          count: BRANCH_PAGE_SIZE,
        },
        append ? (existing?.cursor ?? undefined) : undefined,
      );
      const items = page.items ?? [];
      const declaredLeaves: string[] = [];
      for (const item of items) {
        this.cacheNode(this.toNode(item));
        if (item.hasSubcompanies === false && item.id) {
          declaredLeaves.push(item.id);
        }
      }

      const childIds = append
        ? [...(existing?.children ?? []), ...items.map((item) => item.id)]
        : items.map((item) => item.id);

      if ((!append && items.length === 0) || declaredLeaves.length > 0) {
        this.knownLeaves.update((set) => {
          const next = new Set(set);
          if (!append && items.length === 0) {
            next.add(id);
          }
          for (const leafId of declaredLeaves) {
            next.add(leafId);
          }
          return next;
        });
      }

      this.setBranch(id, {
        status: 'loaded',
        children: childIds,
        cursor: page.cursor || page.nextPageToken || null,
      });
    } catch (error) {
      console.error('Failed to load companies.', error);
      if (refresh) {
        throw error;
      }
      if (this.isForbiddenOrNotFound(error)) {
        this.forgetRevokedBranch(id);
      }
      this.setBranchError(id, error);
      throw error;
    }
  }

  /**
   * Drops a branch the user no longer has access to from both storage and the
   * in-memory set. Storage alone is not enough: `persistExpanded` serializes the
   * whole set, so the next interaction would write the revoked id straight back.
   */
  private forgetRevokedBranch(id: string): void {
    this.expanded.update((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });

    this.searchExpanded.update((current) => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Set(current);
      next.delete(id);
      return next;
    });

    const userCompanyId = this.userCompanyId();
    if (userCompanyId) {
      this.expansion.prune(userCompanyId, id);
    }
  }

  private setBranchError(id: string, error: unknown): void {
    const existing = this.branches().get(id);
    this.setBranch(id, {
      status: 'error',
      children: existing?.children ?? [],
      cursor: existing?.cursor ?? null,
      errorMessage: this.listErrorHandler.processErrorCode(
        'Companies',
        'load',
        error,
      ),
    });
  }

  private projectRows(): CompanyTreeRow[] {
    const rootId = this.userCompanyId();
    if (!rootId || !this.nodes().get(rootId)) {
      return [];
    }

    const rows: CompanyTreeRow[] = [];
    this.visitNode(rootId, 1, 1, 1, rows, new Set<string>(), {
      isExpanded: (id) => id === rootId || this.expanded().has(id),
      variantForLevel: (level) => (level === 1 ? 'root' : 'branch'),
      childIdsOf: (id) => this.visibleChildIds(id),
      hasHiddenChildren: (id) => this.hasHiddenChildren(id),
      canHideChildren: (id) => this.canHideChildren(id),
      reserveRootControlStep: this.chain() !== null,
    });
    return rows;
  }

  /**
   * Projects search matches into the same row shape the tree uses, so the
   * search surface can reuse every branch the tree already loaded. Each
   * match is its own level-1 entry point; `visitNode` is not shared state
   * across them, so one match's subtree cannot be mistaken for another's.
   */
  projectSearchRows(matches: readonly CompanyNode[]): CompanyTreeRow[] {
    const rows: CompanyTreeRow[] = [];
    const ancestors = new Set<string>();
    const setSize = matches.length;

    matches.forEach((match, index) => {
      if (!match.id) {
        return;
      }

      this.visitNode(match.id, 1, index + 1, setSize, rows, ancestors, {
        isExpanded: (id) => this.searchExpanded().has(id),
        variantForLevel: () => 'search',
        matchNode: match,
      });
    });

    return rows;
  }

  private visitNode(
    id: string,
    level: number,
    posInSet: number,
    setSize: number,
    rows: CompanyTreeRow[],
    ancestors: Set<string>,
    options: {
      isExpanded: (id: string) => boolean;
      variantForLevel: (level: number) => CompanyTreeRow['variant'];
      parentKey?: string;
      /**
       * Supplied only for a search match's own row: matches are not in the
       * node cache (it would later overwrite the match's `parentName` with a
       * cached node lacking it), so the match itself is the row's source.
       */
      matchNode?: CompanyNode;
      /**
       * The children a branch shows, in the tree projection only. Absent for
       * search, which always shows `branch.children` and never hides a
       * sibling.
       */
      childIdsOf?: (id: string) => string[];
      hasHiddenChildren?: (id: string) => boolean;
      canHideChildren?: (id: string) => boolean;
      /** Constant for the whole projection; true only in the branch view. */
      reserveRootControlStep?: boolean;
    },
  ): void {
    const node = options.matchNode ?? this.renderableNode(id, ancestors);
    if (!node) {
      return;
    }

    const key = options.parentKey ? `${options.parentKey}/${id}` : id;
    // Expanded by projection rather than by relying only on `expanded`: the
    // root row renders expanded from the moment `userCompanyId` is known, even
    // in the instant before `applyExpansion` seeds the set.
    const expanded = options.isExpanded(id);
    const variant = options.variantForLevel(level);
    const branch = this.branches().get(id);
    const selectedId = this.selectedCompanyId();
    const selectedPath = this.selectedPath();
    const hiddenChildren = options.hasHiddenChildren?.(id) ?? false;
    const canHideChildren = options.canHideChildren?.(id) ?? false;

    const row: CompanyTreeRow = {
      id: node.id,
      key,
      name: node.name,
      address: node.address,
      level,
      posInSet,
      setSize,
      variant,
      expandable: !this.knownLeaves().has(id),
      expanded,
      selected: node.id === selectedId,
      containsSelected: selectedPath.includes(node.id) && !expanded,
      // A list hidden behind "…" has nothing on screen to load, page, retry or
      // announce a spinner or an error for.
      loading:
        !hiddenChildren &&
        branch?.status === 'loading' &&
        branch.children.length === 0,
      loadingMore:
        expanded &&
        !hiddenChildren &&
        branch?.status === 'loading' &&
        branch.children.length > 0,
      errorMessage: hiddenChildren ? null : (branch?.errorMessage ?? null),
      hasMore: expanded && !!branch?.cursor && !hiddenChildren,
      hiddenChildren,
      canHideChildren,
      reserveRootControlStep: options.reserveRootControlStep ?? false,
    };

    if (options.matchNode?.parentName) {
      row.parentName = options.matchNode.parentName;
    }

    rows.push(row);

    if (!expanded) {
      return;
    }

    const children = options.childIdsOf
      ? options.childIdsOf(id)
      : (branch?.children ?? []);
    ancestors.add(id);
    // Children this visit would skip emit no row, so counting them would
    // inflate setSize and shift every following sibling's posInSet. The
    // predicate must match the early return above, and it must run after
    // `ancestors.add(id)` so a self-referential child is excluded too.
    const renderableChildren = children.filter(
      (childId) => !!this.renderableNode(childId, ancestors),
    );
    // A branch with pages left to load has an unknown sibling count, which ARIA
    // spells -1 rather than the number loaded so far.
    const childSetSize = branch?.cursor ? -1 : renderableChildren.length;
    // The lone path child under an unrevealed chain member reports its place
    // in the full sibling set (FR-107), not in this filtered single-child
    // list: `aria-setsize` exists for sets that are not all in the DOM, and a
    // screen reader user hears "3 of 12" instead of a false "1 of 1".
    const unrevealedChainMember =
      options.childIdsOf && !this.revealedState().has(id)
        ? this.chainMemberInfo(id)
        : null;
    const fullChildren = branch?.children ?? [];
    renderableChildren.forEach((childId, index) => {
      let childPosInSet = index + 1;
      let childSize = childSetSize;

      if (unrevealedChainMember) {
        const fullIndex = fullChildren.indexOf(childId);
        if (fullIndex >= 0) {
          childPosInSet = fullIndex + 1;
          childSize = branch?.cursor ? -1 : fullChildren.length;
        } else {
          childPosInSet = fullChildren.length + 1;
          childSize = -1;
        }
      }

      this.visitNode(
        childId,
        level + 1,
        childPosInSet,
        childSize,
        rows,
        ancestors,
        {
          isExpanded: options.isExpanded,
          variantForLevel: options.variantForLevel,
          parentKey: key,
          childIdsOf: options.childIdsOf,
          hasHiddenChildren: options.hasHiddenChildren,
          reserveRootControlStep: options.reserveRootControlStep,
        },
      );
    });
    ancestors.delete(id);
  }

  /** The node a visit would render, or null when the visit would skip it. */
  private renderableNode(
    id: string,
    ancestors: Set<string>,
  ): CompanyNode | null {
    if (ancestors.has(id)) {
      return null;
    }

    return this.nodes().get(id) ?? null;
  }

  private toNode(company: CompanyLike, isRoot = false): CompanyNode {
    return {
      id: company.id ?? '',
      name: company.name ?? '',
      parentId: isRoot ? null : (company.parentId ?? null),
      address: this.addressUtils.formatShortAddress(company),
    };
  }

  private cacheNode(node: CompanyNode): void {
    if (!node.id) {
      return;
    }

    this.nodes.update((map) => {
      const next = new Map(map);
      next.set(node.id, node);
      return next;
    });
  }

  private setBranch(id: string, state: BranchState): void {
    this.branches.update((map) => {
      const next = new Map(map);
      next.set(id, state);
      return next;
    });
  }

  private markExpanded(id: string): void {
    const next = new Set(this.expanded());
    next.delete(id);
    next.add(id);
    this.expanded.set(next);
  }

  private persistExpanded(): void {
    const userCompanyId = this.userCompanyId();
    if (!userCompanyId) {
      return;
    }

    this.expansion.write(userCompanyId, [...this.expanded()]);
  }

  private ancestorsOf(id: string): string[] {
    const userCompanyId = this.userCompanyId();
    if (!userCompanyId || id === userCompanyId) {
      return [];
    }

    const path: string[] = [];
    // A hierarchy that contains one of its own ancestors would otherwise walk
    // the same ids forever.
    const visited = new Set<string>([id]);
    let current = this.nodes().get(id);
    while (current?.parentId && current.parentId !== userCompanyId) {
      const parentId = current.parentId;
      if (visited.has(parentId)) {
        break;
      }
      visited.add(parentId);
      path.unshift(parentId);
      current = this.nodes().get(parentId);
      if (!current) {
        break;
      }
    }

    if (!path.includes(userCompanyId)) {
      path.unshift(userCompanyId);
    }

    return path;
  }

  private isForbiddenOrNotFound(error: unknown): boolean {
    const status = this.httpStatus(error);
    return status === 403 || status === 404;
  }

  private httpStatus(error: unknown): number | undefined {
    const extracted = getError(error);
    if (typeof extracted?.status === 'number') {
      return extracted.status;
    }

    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: unknown }).status;
      return typeof status === 'number' ? status : undefined;
    }

    return undefined;
  }
}
